import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Tooltip,
  TableSortLabel,
  Typography,
  Button,
} from '@mui/material';
import { EditOutlined, DeleteOutlined, Business } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useSortTable from '../../../hooks/useSortTable';

const CompanyTable = ({ companies, onEdit, onDelete, page, onPageChange, pagination, canEdit, canDelete }) => {
  const navigate = useNavigate();
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(companies);

  if (!companies || companies.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Business sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay empresas registradas</p>
      </Paper>
    );
  }

  const SortCell = ({ label, field, align, sx: extraSx }) => (
    <TableCell align={align} onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const totalPages = pagination ? Math.ceil((pagination.total || 0) / (pagination.limit || 10)) : 1;
  const from = pagination ? ((page - 1) * (pagination.limit || 10)) + 1 : 1;

  return (
    <Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortCell label="Nombre" field="name" />
                <SortCell label="Identificador" field="identifier" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <SortCell label="Nombre Corto" field="short" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                <SortCell label="Email" field="email" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
                <SortCell label="Usuarios" field="_count.users" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <SortCell label="Sitio Web" field="website" sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((company) => (
                <TableRow
                  key={company.id}
                  hover
                  onClick={() => navigate(`/companies/${company.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                      {company.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{company.identifier}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{company.short}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{company.email || '-'}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{company._count?.users || 0}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    {company.website ? (
                      <Typography
                        variant="body2"
                        component="a"
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: '#5980a6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {company.website}
                      </Typography>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      {canEdit && (
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEdit(company); }}
                            sx={{ color: '#5d5d60', width: 36, height: 36 }}
                          >
                            <EditOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onDelete(company.id); }}
                            sx={{ color: '#5d5d60', width: 36, height: 36 }}
                          >
                            <DeleteOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {pagination && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
            Mostrando {from} de {pagination.total || 0} empresas
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => onPageChange(page - 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
              Anterior
            </Button>
            <Button variant="outlined" size="small" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
              Siguiente
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CompanyTable;
